import { Component } from '@theme/component';
import { removeTrapFocus } from '@theme/focus';
import { lockScroll, onAnimationEnd } from '@theme/utilities';

/** Horizon's squeeze breakpoint (theme-drawer.js MODAL_BREAKPOINT). */
const SQUEEZE_QUERY = '(min-width: 990px)';

/** sessionStorage key Horizon's restore script reads in theme-drawer.liquid. */
const RESTORE_KEY = 'theme-drawer-open';

/** Our slide-in class (wld-global-styles). Horizon's own opening animation is cancelled by the modal swap. */
const ENTERING_CLASS = 'wld-drawer--entering';

/**
 * wld-drawer-overlay — every <theme-drawer> slides in OVER the page, at every width.
 *
 * Horizon opens drawers on ≥990px as a non-modal sidebar that squeezes `.page-wrapper` aside
 * and re-opens them after navigation. RM-Poolen never wants that. Without touching Horizon:
 * - CSS (wld-global-styles) cancels the squeeze margin, so the panel overlays the content;
 * - here, a drawer opened on desktop is re-shown as a modal — the same path Horizon takes below
 *   990px: ::backdrop, click-outside and Escape to close, scroll lock, native focus trap;
 * - the open-drawer state is never restored on the next page.
 * The slide-in / slide-out and backdrop fade are ours too (wld-global-styles).
 */
class WldDrawerOverlay extends Component {
  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia(SQUEEZE_QUERY);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange);
    window.addEventListener('pagehide', this.#forgetOpenDrawers);

    this.#forgetOpenDrawers();
    this.#closeRestoredDrawers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.#desktopQuery.removeEventListener('change', this.#onBreakpointChange);
    window.removeEventListener('pagehide', this.#forgetOpenDrawers);
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (!this.#desktopQuery.matches) return;
    const drawer = /** @type {Element} */ (event.target);
    if (drawer instanceof HTMLElement && drawer.localName === 'theme-drawer') this.#showAsModal(drawer, true);
  };

  /** Horizon re-shows open drawers non-modal when the viewport crosses 990px; take them back. */
  #onBreakpointChange = () => {
    if (!this.#desktopQuery.matches) return;
    requestAnimationFrame(() => {
      for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
        if (drawer instanceof HTMLElement) this.#showAsModal(drawer, false);
      }
    });
  };

  /**
   * Re-opens the drawer's panel with showModal(). Also used on an already-modal panel, where it
   * moves the panel to the top of the top layer (Horizon's own "bring to front" in modal mode).
   *
   * @param {HTMLElement} drawer
   * @param {boolean} animate - Slide in (open, bring to front); false when only the mode changes.
   */
  #showAsModal(drawer, animate) {
    const panel = drawer.querySelector(':scope > dialog.theme-drawer__dialog');
    if (!(panel instanceof HTMLDialogElement) || !panel.open) return;

    panel.close();
    lockScroll(panel);
    panel.showModal();
    removeTrapFocus();

    if (animate) {
      panel.classList.remove(ENTERING_CLASS);
      panel.classList.add(ENTERING_CLASS);
      onAnimationEnd(panel, () => panel.classList.remove(ENTERING_CLASS), { subtree: false });
    }

    /** @type {HTMLElement | null} */ (panel.querySelector('.theme-drawer__close-button'))?.focus();
  }

  #forgetOpenDrawers = () => {
    try {
      sessionStorage.removeItem(RESTORE_KEY);
    } catch {
      /* storage unavailable — nothing to restore either */
    }
  };

  /** Closes a drawer Horizon's pre-paint script restored before this module ran. */
  async #closeRestoredDrawers() {
    if (!document.querySelector('theme-drawer[open]')) return;
    await customElements.whenDefined('theme-drawer');
    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      /** @type {HTMLElement & { close?: () => void }} */ (drawer).close?.();
    }
  }
}

if (!customElements.get('wld-drawer-overlay')) {
  customElements.define('wld-drawer-overlay', WldDrawerOverlay);
}
